import React, { useState, useCallback, useRef } from 'react';
import { showSpinner, hideSpinner } from '../Actions/LoadingSpinner.action';
import { useDispatch } from 'react-redux';
import {
  useFloating,
  useInteractions,
  useClick,
  useDismiss,
  offset,
  flip,
  shift,
  FloatingPortal,
} from '@floating-ui/react';

const ANIMATION_DURATION = 2000;
const MIN_LOADING_TIME = 1000;

/**
 * EnhancedBuyButton component
 *
 * A button component that handles click events with loading, success, and error states.
 * It ensures a minimum loading time for better user experience.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} props.stylePrefix - The prefix for the button's CSS classes
 * @param {string} props.label - The text for the button
 * @param {string} props.href - The href if link
 * @param {boolean} props.siteLoader - Site wide loader overlay
 * @param {string} props.cssClass - Additional CSS classes for the button
 * @param {Function} props.onClick - Callback function to be executed on button click
 *                                   Should return false for validation failure or a Promise for async operations
 * @param {Function} props.onClickFinished - Callback function to be executed after button click is awaited
 * @param {string} props.disabledTooltip - Tooltip when click on visuallyDisabled button
 * @param {boolean} props.visuallyDisabled - fake disable on button to allow tooltip
 * @returns {JSX.Element} The rendered EnhancedBuyButton component
 *
 * @example
 * <EnhancedBuyButton
 *   stylePrefix="buy-button"
 *   label="Buy"
 *   href="/something"
 *   siteLoader={true}
 *   cssClass="button button--cta"
 *   onClick={() => {
 *     if (validationFails) return false;
 *     return asyncOperation();
 *   }}
 *  disabledTooltip="Something"
 * />
 */
const EnhancedBuyButton = ({
  stylePrefix = 'buy-button',
  label,
  href,
  cssClass,
  onClick,
  onClickFinished,
  siteLoader,
  disabled,
  disabledTooltip,
  visuallyDisabled
}) => {
  const timeoutRef = useRef(null);
  const [state, setState] = useState('neutral');
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const dispatch = useDispatch();

  const { refs, floatingStyles, context } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    middleware: [offset(10), flip(), shift()],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleClick = useCallback(async (e) => {
    if (visuallyDisabled && disabledTooltip) {
      e.preventDefault();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsTooltipOpen(true);
      timeoutRef.current = setTimeout(() => {
        setIsTooltipOpen(false);
        timeoutRef.current = null;
      }, 2500);
      return;
    }

    if (!visuallyDisabled) {
      const result = onClick(e);

      if (result === false) {
        return;
      }

      siteLoader ? dispatch(showSpinner()) : setState('loading');
      const startTime = Date.now();

      try {
        const success = await result;
        await ensureMinLoadingTime(startTime);

        if (!siteLoader) {
          setState(success ? 'success' : 'error');
        }
      } catch {
        await ensureMinLoadingTime(startTime);
        if (!siteLoader) setState('error');
      } finally {
        siteLoader
          ? dispatch(hideSpinner())
          : setTimeout(() => setState('neutral'), ANIMATION_DURATION);
          handleClickFinished(e);
      }
    }
  }, [onClick, isTooltipOpen, disabled, disabledTooltip]);

  const handleClickFinished = useCallback(async (e) => {
    onClickFinished(e);
    return;
  }, [onClickFinished])

  const ensureMinLoadingTime = async (startTime) => {
    const elapsedTime = Date.now() - startTime;
    if (elapsedTime < MIN_LOADING_TIME) {
      await new Promise((resolve) =>
        setTimeout(resolve, MIN_LOADING_TIME - elapsedTime)
      );
    }
  };

  const buttonElement = href ? (
    <a 
      className={cssClass} 
      href={href} 
      rel="nofollow"
    >
      {label}
    </a>
  ) : (
    <button
      ref={refs.setReference}
      {...getReferenceProps()}
      onClick={handleClick}
      disabled={visuallyDisabled ? undefined : disabled}
      aria-disabled={visuallyDisabled ? 'true' : undefined}
      className={`enhanced-buy-button ${cssClass} ${visuallyDisabled ? 'button--disabled' : ''}`}
    >
      {label}
    </button>
  );

  return (
    <span className={`${stylePrefix}--${state}`}>
      {buttonElement}
      
      {isTooltipOpen && disabled && disabledTooltip && (
        <FloatingPortal>
          <span
            className="button__tooltip"
            ref={refs.setFloating}
            style={{
              ...floatingStyles
            }}
            {...getFloatingProps()}
          >
            {disabledTooltip}
          </span>
        </FloatingPortal>
      )}
    </span>
  );
};

export default EnhancedBuyButton;
